/* eslint-disable @typescript-eslint/no-magic-numbers */
import React from 'react';
import { useForm } from 'react-hook-form';
import { sleep } from '@cp/utils/sleep';
import loadable from '@loadable/component';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { AppFormProvider } from '@cp/utils/form';
import { parse } from 'date-fns';
import { dateObjToDateOnlyISOString } from '@cp/shared/lib';
import { FormAvailableOnlyFieldName } from 'src/entities/catalog/ui/availableOnly/model';
import { CatalogSortingTypes } from 'src/entities/catalog/model/types';
import { CatalogNavResumeFormModel } from '../model';
import { getDefaultValues } from '../model/utils/getDefaultValues';
import { FormFieldName, FormSubmitTrigger } from '../model/form';
import { WatcherProps } from '../model/watcher.interface';
import { Watcher } from '../model/watcher';
import { DATE_VALUE_FORMAT } from '../model/const';
import { CatalogNavResumeProps, CatalogNavResumeViewProps } from './model';

// const ViewDesktop = loadable(() => import('./view.desktop'));
// const ViewMobile = loadable(() => import('./view.mobile'));
const ViewDesktop = loadable(() => import('./view.desktop.new'));
const ViewMobile = loadable(() => import('./view.mobile.new'));

export const CatalogNavResume: React.FC<CatalogNavResumeProps> = ({
  careerId,
  sx,
  onChange,
  onChangeCity,
  onChangeSorting,
  onMapButtonClick,
  defaultCity,
  defaultFilter,
  defaultSorting,
  careerDictionary,
}) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT_THRESHOLD));
  const defaultValues = React.useMemo(
    () => getDefaultValues(defaultCity, defaultFilter ?? {}, defaultSorting),
    [defaultCity, defaultFilter, defaultSorting],
  );

  const form = useForm<CatalogNavResumeFormModel>({
    defaultValues,
  });

  const submit = React.useCallback<FormSubmitTrigger>(async () => {
    await sleep(0);
    const values = form.getValues();

    if (onChange) {
      const scheduleDate = values[FormAvailableOnlyFieldName.Date]
        ? dateObjToDateOnlyISOString(parse(values[FormAvailableOnlyFieldName.Date] as string, DATE_VALUE_FORMAT, new Date()))
        : undefined;
      const scheduleTimeFrom = values[FormAvailableOnlyFieldName.Start] ?? undefined;
      const scheduleTimeTo = values[FormAvailableOnlyFieldName.End] ?? undefined;

      onChange({
        date: scheduleDate,
        start: scheduleTimeFrom,
        end: scheduleTimeTo,
      });
    }

    if (onChangeSorting) {
      const sorting = values[FormFieldName.Sorters] || CatalogSortingTypes.Normal;
      onChangeSorting(sorting);
    }
  }, [form, onChange, onChangeSorting]);

  const watcherProps: WatcherProps = { defaultCity, form, onChangeCity, submit, defaultValues };

  const viewProps: CatalogNavResumeViewProps = {
    careerId,
    form,
    submit,
    onMapButtonClick,
    sx,
    careerDictionary,
  };

  const View = isMobileScreen ? ViewMobile : ViewDesktop;

  return (
    <AppFormProvider form={form}>
      <Watcher {...watcherProps} />
      <View {...viewProps} />
    </AppFormProvider>
  );
};
